import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/common/PasswordInput";
import { PasswordStrength } from "@/components/common/PasswordStrength";
import { api, apiError } from "@/lib/api";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [token, setToken] = useState(params.get("token") || "");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
            setError("Password must be at least 8 characters and include letters and numbers.");
            return;
        }
        setLoading(true); setError("");
        try {
            await api.post("/auth/reset-password", { token, password });
            toast.success("Password updated. Please sign in.");
            navigate("/login");
        } catch (err) {
            setError(apiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout title="Set new password" subtitle="Enter your reset token and a new password.">
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reset Token</Label>
                    <Input data-testid="reset-token" required value={token} onChange={(e) => setToken(e.target.value)}
                        placeholder="Paste your reset token" className="bg-surface border-border rounded-sm h-11 font-mono text-xs" />
                </div>
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">New Password</Label>
                    <PasswordInput testid="reset-password" value={password} onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password" placeholder="Create a strong password" />
                    <PasswordStrength value={password} />
                </div>
                {error && <div className="text-sm text-alert font-mono">{error}</div>}
                <button data-testid="reset-submit" disabled={loading} type="submit"
                    className="w-full h-11 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />} Update password
                </button>
                <p className="text-sm text-muted-foreground text-center">
                    <Link to="/login" className="text-volt hover:underline">Back to sign in</Link>
                </p>
            </form>
        </AuthLayout>
    );
}
